import { FormEvent, useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ArrowLeft, 
  UserPlus, 
  Save, 
  Mail, 
  Phone, 
  Building, 
  Shield, 
  User,
  Key,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff,
  RefreshCw,
  ChevronRight,
  ChevronLeft
} from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Badge } from '@/components/ui/badge'
import { Switch } from '@/components/ui/switch'
import { Progress } from '@/components/ui/progress'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { useAdmin } from '@/hooks/use-admin'
import { formatPhone } from '@/lib/utils'

export type TipoPermissao = 'admin' | 'gestor' | 'lider' | 'membro'

export interface Igreja {
  id: string
  nome: string
  endereco: string
  cidade: string
  estado: string
  cep: string
  telefone: string
  email: string
  ativa: boolean
  criado_em: string
}

export interface NovoUsuarioForm {
  nome: string
  email: string
  telefone: string
  igreja_id: string
  permissao: TipoPermissao
  ativo: boolean
  senha_temporaria: string
}

  const NOVO_USUARIO_DEFAULT: NovoUsuarioForm = {
    nome: '',
    email: '',
    telefone: '',
    igreja_id: '',
    permissao: 'membro',
    ativo: true,
    senha_temporaria: '123456', // Senha padrão conforme solicitado
  }

// Mock data - em produção viria de uma API
// Removido mockIgrejas - agora usando dados reais do Supabase

const PERMISSAO_LABELS: Record<TipoPermissao, string> = {
  admin: 'Administrador',
  gestor: 'Gestor',
  lider: 'Líder',
  membro: 'Membro'
}

const PERMISSAO_STYLES: Record<TipoPermissao, string> = {
  admin: 'bg-red-500/15 text-red-600 ring-1 ring-inset ring-red-500/30',
  gestor: 'bg-blue-500/15 text-blue-600 ring-1 ring-inset ring-blue-500/30',
  lider: 'bg-emerald-500/15 text-emerald-600 ring-1 ring-inset ring-emerald-500/30',
  membro: 'bg-gray-500/15 text-gray-600 ring-1 ring-inset ring-gray-500/30'
}

const PERMISSAO_DESCRIPTIONS: Record<TipoPermissao, string> = {
  admin: 'Acesso total ao sistema, pode gerenciar todas as funcionalidades',
  gestor: 'Pode gerenciar ministérios, eventos e membros da igreja',
  lider: 'Pode gerenciar seu ministério e participar de eventos',
  membro: 'Acesso básico para participar de eventos e atividades'
}

const STEPS = [
  { id: 1, title: 'Dados Pessoais', description: 'Informações básicas do usuário' },
  { id: 2, title: 'Igreja & Permissões', description: 'Vinculação e nível de acesso' },
  { id: 3, title: 'Acesso & Senha', description: 'Configurações de login' },
  { id: 4, title: 'Revisão', description: 'Confirme os dados antes de salvar' }
]

function gerarSenhaTemporaria() {
  const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%'
  let senha = ''
  for (let i = 0; i < 12; i++) {
    senha += chars.charAt(Math.floor(Math.random() * chars.length))
  }
  return senha
}

export function UsuarioNovoPage() {
  const navigate = useNavigate()
  const { igrejas } = useAdmin()
  const [currentStep, setCurrentStep] = useState(1)
  const [formState, setFormState] = useState<NovoUsuarioForm>(NOVO_USUARIO_DEFAULT)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [errors, setErrors] = useState<Partial<Record<keyof NovoUsuarioForm, string>>>({})

  const handleInputChange = (field: keyof NovoUsuarioForm, value: string | boolean | TipoPermissao) => {
    let processedValue = value
    
    // Apply phone mask if field is telefone
    if (field === 'telefone' && typeof value === 'string') {
      processedValue = formatPhone(value)
    }
    
    setFormState(prev => ({ ...prev, [field]: processedValue }))
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  const handleGerarSenha = () => {
    const novaSenha = gerarSenhaTemporaria()
    handleInputChange('senha_temporaria', novaSenha)
  }

  const selectedIgreja = useMemo(() => {
    return igrejas.find(igreja => igreja.id === formState.igreja_id)
  }, [formState.igreja_id, igrejas])

  const validateStep = (step: number): boolean => {
    const newErrors: Partial<Record<keyof NovoUsuarioForm, string>> = {}

    switch (step) {
      case 1:
        if (!formState.nome.trim()) newErrors.nome = 'Nome é obrigatório'
        if (!formState.email.trim()) newErrors.email = 'Email é obrigatório'
        else if (!/\S+@\S+\.\S+/.test(formState.email)) newErrors.email = 'Email inválido'
        break
      case 2:
        if (!formState.igreja_id) newErrors.igreja_id = 'Selecione uma igreja'
        break
      case 3:
        if (!formState.senha_temporaria.trim()) newErrors.senha_temporaria = 'Senha temporária é obrigatória'
        else if (formState.senha_temporaria.length < 6) newErrors.senha_temporaria = 'Senha deve ter pelo menos 6 caracteres'
        break
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, STEPS.length))
    }
  }

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1))
  }

  const handleFormSubmit = (e: FormEvent) => {
    e.preventDefault()
    // Previne submit automático - apenas navega para o próximo step se não estiver no último
    if (currentStep < STEPS.length) {
      if (validateStep(currentStep)) {
        nextStep()
      }
    }
    // No último step, não faz nada - o usuário deve clicar no botão específico
  }

  const handleFinalSubmit = async () => {
    // Validação final antes do envio
    if (!validateStep(currentStep)) return

    setIsSubmitting(true)
    
    try {
      // Chamar a edge function para criar o usuário
      const response = await fetch('https://tkmsdopgnwyuuwphmczm.supabase.co/functions/v1/criar-usuario', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6InRrbXNkb3Bnbnd5dXV3cGhtY3ptIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjEzMTYwODksImV4cCI6MjA3Njg5MjA4OX0.QMHxTzB7uherl4I8HLIUaqT_-_Q18XlkPW40Q8TPr2E`,
        },
        body: JSON.stringify({
          nome: formState.nome,
          email: formState.email,
          telefone: formState.telefone,
          igreja_id: formState.igreja_id,
          permissao: formState.permissao,
          ativo: formState.ativo,
          senha_temporaria: formState.senha_temporaria
        })
      })

      const result = await response.json()

      if (!response.ok || !result.success) {
        throw new Error(result.error || 'Erro ao criar usuário')
      }

      console.log('Usuário criado com sucesso:', result)
      navigate('/admin-app')
    } catch (error) {
      console.error('Erro ao criar usuário:', error)
      // TODO: Mostrar erro para o usuário
    } finally {
      setIsSubmitting(false)
    }
  }

  const progress = (currentStep / STEPS.length) * 100

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-background to-indigo-50 dark:from-slate-900 dark:via-background dark:to-slate-800 p-4">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin-app')}
              className="gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Voltar
            </Button>
          </div>

          <div className="text-center space-y-2">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-full mb-4">
              <UserPlus className="h-8 w-8 text-primary" />
            </div>
            <h1 className="text-3xl font-bold text-foreground">Novo Usuário</h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Crie um novo usuário para o sistema. Preencha as informações em etapas para garantir que todos os dados estejam corretos.
            </p>
          </div>

          {/* Progress */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm font-medium text-foreground">
                Etapa {currentStep} de {STEPS.length}
              </span>
              <span className="text-sm text-muted-foreground">
                {Math.round(progress)}% concluído
              </span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>

          {/* Steps */}
          <div className="mt-6 flex justify-between">
            {STEPS.map((step, index) => (
              <div
                key={step.id}
                className={`flex-1 ${index < STEPS.length - 1 ? 'border-r border-border pr-4' : ''}`}
              >
                <div className="flex items-center">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium ${
                      step.id < currentStep
                        ? 'bg-green-500 text-white'
                        : step.id === currentStep
                        ? 'bg-primary text-white'
                      : 'bg-muted text-muted-foreground'
                    }`}
                  >
                    {step.id < currentStep ? (
                      <CheckCircle className="h-4 w-4" />
                    ) : (
                      step.id
                    )}
                  </div>
                  <div className="ml-3 hidden sm:block">
                    <p className={`text-sm font-medium ${
                      step.id <= currentStep ? 'text-foreground' : 'text-muted-foreground'
                    }`}>
                      {step.title}
                    </p>
                    <p className="text-xs text-muted-foreground">{step.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </motion.div>

        {/* Form */}
        <form onSubmit={handleFormSubmit}>
          <AnimatePresence mode="wait">
            {/* Step 1: Dados Pessoais */}
            {currentStep === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <User className="h-5 w-5 text-primary" />
                      Dados Pessoais
                    </CardTitle>
                    <CardDescription>
                      Informe os dados básicos do usuário
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div className="space-y-2">
                        <Label htmlFor="nome">Nome Completo *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="nome"
                            value={formState.nome}
                            onChange={(e) => handleInputChange('nome', e.target.value)}
                            placeholder="Digite o nome completo"
                            className={`pl-10 ${errors.nome ? 'border-red-500' : ''}`}
                          />
                        </div>
                        {errors.nome && (
                          <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.nome}
                          </p>
                        )}
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="email">Email *</Label>
                        <div className="relative">
                          <Mail className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="email"
                            type="email"
                            value={formState.email}
                            onChange={(e) => handleInputChange('email', e.target.value)}
                            placeholder="usuario@exemplo.com"
                            className={`pl-10 ${errors.email ? 'border-red-500' : ''}`}
                          />
                        </div>
                        {errors.email && (
                          <p className="text-sm text-red-600 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {errors.email}
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="telefone">Telefone</Label>
                      <div className="relative">
                        <Phone className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                          id="telefone"
                          value={formState.telefone}
                          onChange={(e) => handleInputChange('telefone', e.target.value)}
                          placeholder="(11) 99999-9999"
                          className="pl-10"
                        />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 2: Igreja e Permissões */}
            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Building className="h-5 w-5 text-primary" />
                      Igreja e Permissões
                    </CardTitle>
                    <CardDescription>
                      Vincule o usuário a uma igreja e defina suas permissões
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="space-y-2">
                      <Label htmlFor="igreja">Igreja *</Label>
                      <Select
                        value={formState.igreja_id}
                        onValueChange={(value) => handleInputChange('igreja_id', value)}
                      >
                        <SelectTrigger className={errors.igreja_id ? 'border-red-500' : ''}>
                          <SelectValue placeholder="Selecione uma igreja" />
                        </SelectTrigger>
                        <SelectContent>
                          {igrejas.filter(igreja => igreja.ativa).map((igreja) => (
                            <SelectItem key={igreja.id} value={igreja.id}>
                              {igreja.nome}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {errors.igreja_id && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.igreja_id}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="permissao">Nível de Permissão *</Label>
                      <Select
                        value={formState.permissao}
                        onValueChange={(value) => handleInputChange('permissao', value as TipoPermissao)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Selecione o nível de permissão" />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(PERMISSAO_LABELS).map(([key, label]) => (
                            <SelectItem key={key} value={key}>
                              <div className="flex items-center gap-2">
                                <Badge className={PERMISSAO_STYLES[key as TipoPermissao]} variant="outline">
                                  {label}
                                </Badge>
                              </div>
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-sm text-muted-foreground">
                        {PERMISSAO_DESCRIPTIONS[formState.permissao]}
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                      <div className="space-y-0.5">
                        <Label htmlFor="ativo">Usuário Ativo</Label>
                        <p className="text-sm text-muted-foreground">
                          Usuário pode acessar o sistema imediatamente
                        </p>
                      </div>
                      <Switch
                        id="ativo"
                        checked={formState.ativo}
                        onCheckedChange={(checked) => handleInputChange('ativo', checked)}
                      />
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 3: Acesso e Senha */}
            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Key className="h-5 w-5 text-primary" />
                      Configurações de Acesso
                    </CardTitle>
                    <CardDescription>
                      Defina a senha temporária para o primeiro acesso
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <Alert>
                      <Shield className="h-4 w-4" />
                      <AlertDescription>
                        O usuário deverá alterar esta senha no primeiro acesso ao sistema.
                      </AlertDescription>
                    </Alert>

                    <div className="space-y-2">
                      <Label htmlFor="senha">Senha Temporária *</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Key className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                          <Input
                            id="senha"
                            type={showPassword ? "text" : "password"}
                            value={formState.senha_temporaria}
                            onChange={(e) => handleInputChange('senha_temporaria', e.target.value)}
                            placeholder="Digite ou gere uma senha"
                            className={`pl-10 pr-10 ${errors.senha_temporaria ? 'border-red-500' : ''}`}
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-8 w-8 p-0"
                          >
                            {showPassword ? (
                              <EyeOff className="h-4 w-4" />
                            ) : (
                              <Eye className="h-4 w-4" />
                            )}
                          </Button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={handleGerarSenha}
                          className="gap-2"
                        >
                          <RefreshCw className="h-4 w-4" />
                          Gerar
                        </Button>
                      </div>
                      {errors.senha_temporaria && (
                        <p className="text-sm text-red-600 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {errors.senha_temporaria}
                        </p>
                      )}
                      <p className="text-xs text-muted-foreground">
                        A senha deve ter pelo menos 6 caracteres
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {/* Step 4: Revisão */}
            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5 text-primary" />
                      Revisão dos Dados
                    </CardTitle>
                    <CardDescription>
                      Confirme todas as informações antes de criar o usuário
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Dados Pessoais */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <User className="h-4 w-4" />
                        Dados Pessoais
                      </h4>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 bg-muted/50 rounded-lg">
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Nome</p>
                          <p className="font-medium">{formState.nome}</p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Email</p>
                          <p className="font-medium">{formState.email}</p>
                        </div>
                        {formState.telefone && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Telefone</p>
                            <p className="font-medium">{formState.telefone}</p>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Igreja e Permissões */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <Building className="h-4 w-4" />
                        Igreja e Permissões
                      </h4>
                      <div className="p-4 bg-muted/50 rounded-lg space-y-4">
                        {selectedIgreja && (
                          <div>
                            <p className="text-sm font-medium text-muted-foreground">Igreja</p>
                            <p className="font-medium">{selectedIgreja.nome}</p>
                            <p className="text-sm text-muted-foreground">
                              {selectedIgreja.cidade} - {selectedIgreja.estado}
                            </p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Permissão</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={PERMISSAO_STYLES[formState.permissao]}>
                              {PERMISSAO_LABELS[formState.permissao]}
                            </Badge>
                          </div>
                          <p className="text-sm text-muted-foreground mt-1">
                            {PERMISSAO_DESCRIPTIONS[formState.permissao]}
                          </p>
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Status</p>
                          <span className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full ${
                            formState.ativo 
                              ? 'bg-green-100 text-green-700' 
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {formState.ativo ? (
                              <CheckCircle className="h-3 w-3" />
                            ) : (
                              <AlertCircle className="h-3 w-3" />
                            )}
                            {formState.ativo ? 'Ativo' : 'Inativo'}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Acesso */}
                    <div className="space-y-3">
                      <h4 className="font-semibold text-lg flex items-center gap-2">
                        <Key className="h-4 w-4" />
                        Configurações de Acesso
                      </h4>
                      <div className="p-4 bg-muted/50 rounded-lg">
                        <p className="text-sm font-medium text-muted-foreground">Senha Temporária</p>
                        <code className="text-sm bg-background px-2 py-1 rounded border">
                          {formState.senha_temporaria}
                        </code>
                        <p className="text-xs text-muted-foreground mt-1">
                          O usuário deverá alterar esta senha no primeiro acesso
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Navigation */}
          <div className="flex justify-between mt-8">
            <Button
              type="button"
              variant="outline"
              onClick={currentStep === 1 ? () => navigate('/admin-app') : prevStep}
              className="gap-2"
            >
              <ChevronLeft className="h-4 w-4" />
              {currentStep === 1 ? 'Cancelar' : 'Anterior'}
            </Button>

            {currentStep < STEPS.length ? (
              <Button
                type="button"
                onClick={nextStep}
                className="gap-2"
              >
                Próximo
                <ChevronRight className="h-4 w-4" />
              </Button>
            ) : (
              <Button
                type="button"
                onClick={handleFinalSubmit}
                disabled={isSubmitting}
                className="gap-2"
              >
                <Save className="h-4 w-4" />
                {isSubmitting ? 'Criando...' : 'Criar Usuário'}
              </Button>
            )}
          </div>
        </form>
      </div>
    </div>
  )
}